import { template as template_253545e1e83044d582293a7692c4f68c } from "@ember/template-compiler";
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { isAncestorOf } from 'eflex/util/tree-helpers';
import SidePanel from 'eflex/components/side-panel';
import BsForm from 'eflex/components/bs-form';
import { t } from 'ember-intl';
import { notEq, and, not } from 'ember-truth-helpers';
import NoRouteFitTabs from 'eflex/components/no-route-fit-tabs';
import { hash, fn } from '@ember/helper';
import { scrollIntoView } from 'eflex/modifiers';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import Spinner from 'eflex/components/spinner';
import { eventBus } from 'eflex/helpers';
import BsButton from 'eflex/components/bs-button';
import ContextSelector from 'eflex/components/context-selector';
import TaskDeviceConfig from 'eflex/components/task-device-config';
import TaskContextConfig from 'eflex/components/task-context-config';
import DecisionConfig from 'eflex/components/decision-config';
import TriggerConfigs from 'eflex/components/trigger-configs';
import MaterialConfigs from 'eflex/components/kinetic-material-config';
import JemInstructionsConfiguration from 'eflex/components/jem/instructions/configuration';
import VerticalCollection from '@html-next/vertical-collection/components/vertical-collection/component';
import { isTesting as isTestingMacro, macroCondition } from '@embroider/macros';
import { copyAllToAllContexts, copyEventToSelectedContexts, copyDecisionToSelectedContexts } from 'eflex/services/task-config-repo';
import { copyToSelectedContexts as copyWorkInstructionToSelectedContexts } from 'eflex/services/work-instruction-repo';
import { changeType } from 'eflex/services/task-repo';
import { dedupeTracked } from 'tracked-toolbox';
import { without } from 'ramda';
// eslint-disable-next-line no-unneeded-ternary
const isTesting = macroCondition(isTestingMacro()) ? true : false;
const TASK_CONFIGS_CHUNK_SIZE = 15;
const getTaskConfigsChunked = (taskConfigs, taskConfigsCurrentChunks)=>{
    return taskConfigs.slice(0, taskConfigsCurrentChunks);
};
const getAllTaskConfigsForWorkInstructions = (taskConfigs, context)=>{
    return taskConfigs.filter((_taskConfig)=>{
        return taskConfigIsDisplayed(_taskConfig, context);
    });
};
const taskConfigIsDisplayed = (taskConfig, context)=>{
    let isSelectAll = true;
    if (context != null && Array.isArray(context)) {
        isSelectAll = context.length === 1 && context[0].allSelected;
    }
    switch(true){
        case taskConfig.isDeleted:
            {
                return false;
            }
        case taskConfig.usesComponents && isSelectAll:
            {
                return taskConfig.component === taskConfig.parentComponent;
            }
        case taskConfig.usesComponents:
            {
                return context.includes(taskConfig.configOption);
            }
        case taskConfig.usesOperations:
        case isSelectAll:
            {
                return true;
            }
        default:
            {
                return context.includes(taskConfig.configModel);
            }
    }
};
const onCopyAllToAllContexts = (taskConfigs, selectedContext)=>{
    const toCopy = taskConfigs.find((_taskConfig)=>{
        return taskConfigIsDisplayed(_taskConfig, selectedContext);
    });
    copyAllToAllContexts(toCopy);
};
export default class TaskPanel extends Component {
    @service
    workInstructionCache;
    @service
    defaultTaskConfigContext;
    @tracked
    taskConfigsCurrentChunks = 15;
    @tracked
    selectedContext = [
        this.defaultTaskConfigContext.getContext(this.args.taskParent, {
            allSelected: true
        })
    ];
    @dedupeTracked
    scrollToTop = false;
    previousTask = this.args.task;
    constructor(){
        super(...arguments);
        this.workInstructionCache.setupAsync.perform();
    }
    get disableCopyToAll() {
        return this.args.disabled || this.args.component?.isAlwaysRun || !Array.isArray(this.selectedContext) || this.selectedContext.length !== 1 || this.selectedContext[0].allSelected === true;
    }
    onTreeItemDeleted = (treeItem)=>{
        const treeTask = this.args.task;
        if (treeTask != null && (treeItem === treeTask || isAncestorOf(treeItem, treeTask))) {
            this.args.onCancel();
        }
    };
    onDidUpdate = (elem, [treeTask])=>{
        if (treeTask != null && treeTask !== this.previousTask) {
            this.scrollToTop = true;
        }
        this.previousTask = treeTask;
    };
    contextWasSelected = (context, multiSelectApi)=>{
        if (Array.isArray(context)) {
            this.contextWasSelectedMultiSelect(context, multiSelectApi);
        } else {
            this.contextWasSelectedSingleSelect(context);
        }
    };
    contextWasSelectedMultiSelect = (context, multiSelectApi)=>{
        let newValue = without(this.selectedContext, context)[0];
        const changedComponent = !Array.isArray(this.selectedContext);
        if (changedComponent) {
            this.selectedContext = [
                this.selectedContext
            ];
            newValue = without(this.selectedContext, context)[0];
        }
        const noFilter = newValue?.selectFiltered === true && (multiSelectApi.searchText.length === 0 || multiSelectApi.options.length === multiSelectApi.resultsCount);
        switch(true){
            case newValue?.allSelected === true:
            case noFilter === true:
                {
                    this.selectedContext = multiSelectApi.options.filter(({ allSelected })=>{
                        return allSelected === true;
                    });
                    this.scrollToTop = true;
                    break;
                }
            case newValue?.selectFiltered === true:
                {
                    this.selectedContext = multiSelectApi.results.filter(({ selectFiltered, allSelected })=>{
                        return selectFiltered !== true && allSelected !== true;
                    });
                    break;
                }
            case newValue?.allSelected !== true && newValue?.selectFiltered !== true:
                {
                    this.selectedContext = context.filter(({ selectFiltered, allSelected })=>{
                        return selectFiltered !== true && allSelected !== true;
                    });
                    break;
                }
            default:
                {
                    this.selectedContext = context;
                }
        }
    };
    contextWasSelectedSingleSelect = (context)=>{
        this.selectedContext = context;
    };
    tabBecameActive = (tabName)=>{
        this.scrollToTop = true;
        this.args.tabBecameActive(tabName);
    };
    loadMoreInstructions = (taskConfigsLength, taskConfigsChunkedLength)=>{
        if (taskConfigsChunkedLength < taskConfigsLength) {
            this.taskConfigsCurrentChunks += TASK_CONFIGS_CHUNK_SIZE;
        }
    };
    static{
        template_253545e1e83044d582293a7692c4f68c(`
    <SidePanel
      @header={{t "taskConfig"}}
      @subheader={{@taskParent.name}}
      @subheader2={{@task.name}}
      @onClose={{@onConfirm}}
      {{didUpdate this.onDidUpdate @task}}
    >
      {{eventBus deletedTreeItem=this.onTreeItemDeleted}}

      {{#if @isLoading}}
        <Spinner />
      {{else}}
        <div class="task-panel h-100 d-flex flex-column">
          <BsForm
            class="task-info bg-gray-100 p-3"
            @showValidations={{@showValidations}}
            @model={{@task}}
          >
            {{#if (and @task (not @task.usesOperations))}}
              <div class="row gx-2 form-row align-items-end">
                <div class="col">
                  <ContextSelector
                    @treeItem={{@task}}
                    @renderInPlace={{true}}
                    @disableComponentSelect={{@disabled}}
                    @selectedContext={{this.selectedContext}}
                    @contextWasSelected={{this.contextWasSelected}}
                    @allowAll={{true}}
                    @multiSelect={{true}}
                  />
                </div>
                <div class="col-auto">
                  <label class="form-label"></label>
                  <div>
                    <BsButton
                      @type="secondary"
                      @onClick={{fn onCopyAllToAllContexts @task.taskConfigs this.selectedContext}}
                      @size="sm"
                      @disabled={{this.disableCopyToAll}}
                      class="copy-to-all"
                    >
                      {{#if @task.usesComponents}}
                        {{t "copyToAllOptions"}}
                      {{else}}
                        {{t "copyToAllModels"}}
                      {{/if}}
                    </BsButton>
                  </div>
                </div>
              </div>
            {{/if}}
          </BsForm>
          {{#if @task}}
            <div class="task-panel-tabs d-flex flex-column">
              <NoRouteFitTabs
                @activeTab={{@activeTab}}
                @tabBecameActive={{this.tabBecameActive}}
              as |tabs|>
                <div class="task-tabs-wrapper bg-gray-100">
                  <tabs.navigation class="mb-0" as |nav|>
                    <nav.tab @href="task-device">
                      {{t "plant.task.device"}}
                    </nav.tab>
                    <nav.tab @href="task-context">
                      {{#if @task.usesComponents}}
                        {{t "plant.task.componentSpecificConfiguration"}}
                      {{else if @task.usesOperations}}
                        {{t "plant.task.operationSpecificConfiguration"}}
                      {{else}}
                        {{t "plant.task.modelSpecificConfiguration"}}
                      {{/if}}
                    </nav.tab>
                    {{#unless @task.isDecision}}
                      <nav.tab @href="task-decision">
                        {{t "decisions"}}
                      </nav.tab>
                    {{/unless}}
                    <nav.tab @href="task-event">
                      {{t "events"}}
                    </nav.tab>
                    <nav.tab @href="task-wie">
                      {{t "navigation.plant.editor.workInstructions"}}
                    </nav.tab>
                    {{#if @task.usesOperations}}
                      <nav.tab @href="task-material">
                        {{t "plant.task.material"}}
                      </nav.tab>
                    {{/if}}
                  </tabs.navigation>
                </div>
                <tabs.panes as |panes|>
                  <panes.pane @id="task-device" class="eflex-scroll p-3 pt-4">
                    <div
                      class="task-panes-wrapper flex-column"
                      {{scrollIntoView
                        shouldScroll=this.scrollToTop
                        options=(hash behavior="smooth" top=0)
                      }}
                    >
                      <TaskDeviceConfig
                        @task={{@task}}
                        @taskParent={{@taskParent}}
                        @showValidations={{@showValidations}}
                        @disabled={{@disabled}}
                        @selectedTaskType={{fn changeType @task}}
                      />
                    </div>
                  </panes.pane>
                  <panes.pane @id="task-context" class="eflex-scroll p-3 pt-4">
                    <div class="task-panes-wrapper flex-column">
                      {{#each @task.taskConfigs as |taskConfig i|}}
                        {{#if (taskConfigIsDisplayed taskConfig this.selectedContext)}}
                          <div class="task-context-config">
                            {{#if (notEq i 0)}}
                              <hr />
                            {{/if}}

                            <TaskContextConfig
                              class='pb-2'
                              @task={{@task}}
                              @taskConfig={{taskConfig}}
                              @disabled={{@disabled}}
                              @showValidations={{@showValidations}}
                              @selectedContext={{this.selectedContext}}
                            />
                          </div>
                        {{/if}}
                      {{/each}}
                    </div>
                  </panes.pane>
                  {{#unless @task.isDecision}}
                    <panes.pane @id="task-decision" class="eflex-scroll p-3 pt-4">
                      <div class="task-panes-wrapper flex-column">
                        {{#each @task.taskConfigs as |taskConfig i|}}
                          {{#if (taskConfigIsDisplayed taskConfig this.selectedContext)}}
                            <div class="task-decision-config">
                              {{#if (notEq i 0)}}
                                <hr />
                              {{/if}}
                              <div class="context-name model-specific label fw-semibold pb-3 fs-4">
                                {{taskConfig.configName}}
                              </div>
                              <DecisionConfig
                                @taskConfig={{taskConfig}}
                                @task={{@task}}
                                @disabled={{@disabled}}
                                @showValidations={{@showValidations}}
                              />
                              {{#unless @task.usesOperations}}
                                <div class="d-flex justify-content-end">
                                  <BsButton
                                    class="test-copy-to-contexts"
                                    @type="secondary"
                                    @onClick={{fn copyDecisionToSelectedContexts taskConfig this.selectedContext}}
                                    @disabled={{@task.component.isAlwaysRun}}
                                    @size="sm"
                                  >
                                    {{#if @task.usesComponents}}
                                      {{t "copyOptionSpecificSelected"}}
                                    {{else}}
                                      {{t "copyModelSpecificSelected"}}
                                    {{/if}}
                                  </BsButton>
                                </div>
                              {{/unless}}
                            </div>
                          {{/if}}
                        {{/each}}
                      </div>
                    </panes.pane>
                  {{/unless}}
                  <panes.pane @id="task-event" class="eflex-scroll p-3 pt-4">
                    <div class="task-panes-wrapper flex-column">
                      {{#each @task.taskConfigs as |taskConfig i|}}
                        {{#if (taskConfigIsDisplayed taskConfig this.selectedContext)}}
                          <div class="task-event-config">
                            {{#if (notEq i 0)}}
                              <hr />
                            {{/if}}
                            <div class="context-name model-specific label fw-semibold pb-3 fs-4">
                              {{taskConfig.configName}}
                            </div>
                            <TriggerConfigs
                              @location={{@task}}
                              @triggerConfigs={{taskConfig.triggerConfigs}}
                              @showValidations={{@showValidations}}
                              @disabled={{@disabled}}
                            />
                            {{#unless @task.usesOperations}}
                              <div class="d-flex justify-content-end">
                                <BsButton
                                  @type="secondary"
                                  @onClick={{fn copyEventToSelectedContexts taskConfig this.selectedContext}}
                                  @disabled={{@task.component.isAlwaysRun}}
                                  @size="sm"
                                  class="test-copy-to-contexts"
                                >
                                  {{#if @task.usesComponents}}
                                    {{t "copyOptionSpecificSelected"}}
                                  {{else}}
                                    {{t "copyModelSpecificSelected"}}
                                  {{/if}}
                                </BsButton>
                              </div>
                            {{/unless}}
                          </div>
                        {{/if}}
                      {{/each}}
                    </div>
                  </panes.pane>
                  <panes.pane @id="task-wie" class="eflex-scroll p-3">
                    {{#let
                      (getAllTaskConfigsForWorkInstructions @task.taskConfigs this.selectedContext)
                    as |allTaskConfigs|}}
                      {{#let (getTaskConfigsChunked allTaskConfigs) as |taskConfigsChunked|}}
                        <VerticalCollection
                          @items={{taskConfigsChunked}}
                          @estimateHeight="500px"
                          @lastReached={{
                            fn
                            this.loadMoreInstructions
                            allTaskConfigs.length
                            taskConfigsChunked.length
                          }}
                          @renderAll={{isTesting}}
                        as |taskConfig|>
                          <div class="task-instruction-config mb-3">
                            <JemInstructionsConfiguration
                              @taskConfig={{taskConfig}}
                              @usesComponents={{taskConfig.usesComponents}}
                              @disabled={{@disabled}}
                              @useEvent={{true}}
                              @showCopyToAll={{not @task.usesOperations}}
                              @copyToAll={{fn copyWorkInstructionToSelectedContexts @task}}
                              @selectedContext={{this.selectedContext}}
                            />
                          </div>
                        </VerticalCollection>
                      {{/let}}
                    {{/let}}
                  </panes.pane>
                  <panes.pane @id="task-material" class="eflex-scroll p-3">
                    <div class="task-panes-wrapper flex-column">
                      <MaterialConfigs
                        @task={{@task}}
                        @disabled={{@disabled}}
                        @showValidations={{@showValidations}}
                      />
                    </div>
                  </panes.pane>
                </tabs.panes>
              </NoRouteFitTabs>
            </div>
          {{/if}}
        </div>
      {{/if}}
    </SidePanel>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
